































































































import Component, { mixins } from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { Location } from "vue-router";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { Review } from "@scrile/api-provider/dist/api/ReviewProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import WithAuthUser from "@/mixins/WithAuthUser";
import { AuthorisedOnly } from "@/lib/decorators";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { format, parseISO, timeAgo } from "@/lib/time";
import { getAbsUrl, getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { EVENTS, REVIEW_COUNT_LIMIT } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";
import BaseSpinner from "@/components/BaseSpinner";
import LinkifiedText from "@/components/LinkifiedText";
import Share from "@/components/Share";
import TipIcon from "@/components/TipIcon";
import ContentFeedItemMenu from "../ContentFeedItemMenu";
import ContentFeedLikes from "../ContentFeedLikes";
import ContentFeedPrice from "../ContentFeedPrice";
import ContentFeedReviews from "../ContentFeedReviews";
import FeedReaction from "../FeedReaction";
import FeedReviews from "../FeedReviews";

@Component({
  name: "ContentFeedBaseView",
  components: {
    ContentFeedReviews,
    FeedReviews,
    ContentFeedItemMenu,
    LinkifiedText,
    ContentFeedLikes,
    BaseAvatar,
    BaseSpinner,
    BaseIcon,
    FeedReaction,
    ContentFeedPrice,
    Share,
    TipIcon,
  },
})
export default class ContentFeedBaseView extends mixins(WithAuthUser) {
  @Prop({ type: Object, required: true })
  public readonly contentFeed!: ContentFeed;

  @Prop({ type: Boolean, default: false })
  public readonly showFollowButton!: boolean;

  @Prop({ type: Boolean, default: true })
  public readonly showReportItem!: boolean;

  @Prop({ type: Boolean, default: true })
  public readonly showComment!: boolean;

  @Prop({ type: Boolean, default: true })
  public readonly showFooter!: boolean;

  @Prop({ type: Boolean, default: true })
  public readonly showShare!: boolean;

  @Prop({ type: Array, required: true })
  public readonly subscriptionLevels!: SubscriptionLevel[];

  @Prop({ type: Array, required: true })
  public readonly paymentPackages!: PaymentPackage[];

  @Prop({ type: String })
  public readonly contentClass!: string | undefined;

  public processingFollow: boolean = false;

  public showReactionPopup: boolean = false;

  public showReviewModalCb: (() => void) | null = null;

  public get allowFeedReaction() {
    return config.featureFeedReaction && !this.isAuthor && this.showComment;
  }

  public get allowFeedReviews() {
    return config.featureFeedPublicReview;
  }

  public get isAuthor(): boolean {
    return (this.authUser && this.authUser.id === this.contentFeed.userId) || false;
  }

  public get profile(): Location {
    return getProfileUrl(this.contentFeed.user);
  }

  public get screenName() {
    return this.contentFeed.user.screenName;
  }

  public get createTime() {
    if (this.contentFeed.publishTime) {
      return timeAgo(this.contentFeed.publishTime, { addSuffix: true });
    }
    if (this.contentFeed.processingStatus === "ERROR") {
      return $t("Processing error");
    }
    if (["NEW", "CONVERTING"].indexOf(this.contentFeed.processingStatus) > -1) {
      return $t("Processing...");
    }
    if (this.contentFeed.scheduleTime && parseISO(this.contentFeed.scheduleTime).getTime() >= Date.now()) {
      return $t("Scheduled for {0}", [format(parseISO(this.contentFeed.scheduleTime), "MM.dd.yyyy hh:mm a")]);
    }
    return $t("Not published");
  }

  public get linksValidation() {
    return config.featureClickableUserLinks && this.contentFeed.user.verified;
  }

  public get isAllowExternalLinks() {
    return config.featureClickableUserExternalLinks;
  }

  public get canTip() {
    return config.featureTippingPosts && this.contentFeed.user.id !== this.authUser?.id;
  }

  @AuthorisedOnly
  public onFollow() {
    if (this.processingFollow) return;
    this.processingFollow = true;
    this.$emit("follow", this.contentFeed.userId, () => (this.processingFollow = false));
  }

  public get shareLink() {
    // todo: use router.resolve
    // const route = this.$router.resolve(getProfileUrl(this.contentFeed.user, { feedId: this.contentFeed.id }));
    return getAbsUrl(`/${this.contentFeed.user.username}?feedId=${this.contentFeed.id}`);
  }

  public get reviews() {
    return this.contentFeed?.reviews?.result ?? [];
  }

  public get reviewCount() {
    return this.contentFeed?.reviewStats?.count ?? 0;
  }

  @AuthorisedOnly
  public unlockFeed() {
    emitter.emit(EVENTS.CONTENT_FEED_PURCHASE, this.contentFeed);
  }

  public onDelete(...args: any[]) {
    this.$emit("delete", ...args);
  }

  public addCountReview(reviewCount: number, review: Review) {
    const updatedContentFeed = {
      ...this.contentFeed,
      reviewStats: { ...this.contentFeed.reviewStats, count: reviewCount },
      reviews: {
        ...this.contentFeed.reviews,
        result:
          reviewCount > REVIEW_COUNT_LIMIT
            ? [
                review,
                ...(this.contentFeed.reviews?.result.filter(
                  (item, index) => index !== (this.contentFeed.reviews?.result.length as number) - 1
                ) as Review[]),
              ]
            : [review, ...(this.contentFeed.reviews?.result as Review[])],
      },
    };
    this.$emit("update:contentFeed", updatedContentFeed);
  }

  public decreaseCountReview(reviewCount: number, reviewId: string) {
    const updatedContentFeed = {
      ...this.contentFeed,
      reviewStats: { ...this.contentFeed.reviewStats, count: reviewCount },
      reviews: {
        ...this.contentFeed.reviews,
        result: this.contentFeed.reviews?.result.filter(r => r.id !== reviewId),
      },
    };
    this.$emit("update:contentFeed", updatedContentFeed);
  }
}
